import { Theme, css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link } from 'gatsby-plugin-react-intl'
import { FC } from 'react'

import { useIntlLabel } from '../../../../intl'

const containerStyle = (theme: Theme) =>
    css({
        height: 214,
        padding: theme.spacing(3),
        position: 'relative',
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        textDecoration: 'none',
        margin: theme.spacing(1),
        transition: 'all 0.5s ease',
        flexBasis: '23%',
        '&:after': {
            content: '""',
            width: 0,
            height: 0,
            right: 0,
            bottom: 0,
            position: 'absolute',
            borderLeft: '19px solid transparent',
        },
        '& h3': {
            fontFamily: theme.fonts.default,
            fontSize: 18,
            margin: '0 0 10px 0',
            fontWeight: 700,
        },
        '& p': {
            fontFamily: theme.fonts.default,
            fontSize: 14,
            margin: 0,
            lineHeight: '18px',
            flexGrow: 1,
            overflow: 'hidden',
            marginBottom: theme.spacing(3),
        },
        [theme.breakpoints.down(theme.breakpoints.tablet)]: {
            flexBasis: '31.33%', // 3 per line
        },
        [theme.breakpoints.down(theme.breakpoints.md)]: {
            flexBasis: '47.5%', // 2 per line
        },
        [theme.breakpoints.down(theme.breakpoints.smartphone)]: {
            flexBasis: '98%', // 1 per line
        },
    })

const greyStyle = (theme: Theme) =>
    css({
        backgroundColor: theme.sections.serviceUnpopular.backgroundLight,
        color: theme.sections.serviceUnpopular.foregroundDark,
        '&:after': {
            borderBottom: `19px solid ${theme.sections.servicePresentation.item.backgroundDark}`,
        },
        '&:hover': {
            backgroundColor: theme.sections.serviceUnpopular.backgroundDark,
            color: theme.sections.serviceUnpopular.foregroundLight,
        },
        '& span': {
            backgroundColor: theme.sections.servicePresentation.item.backgroundDark,
            color: theme.sections.serviceUnpopular.foregroundLight,
        },
    })

const greenStyle = (theme: Theme) =>
    css({
        backgroundColor: theme.sections.servicePresentation.item.backgroundDark,
        color: theme.sections.serviceUnpopular.foregroundLight,
        '&:after': {
            borderBottom: `19px solid ${theme.sections.serviceUnpopular.foregroundDark}`,
        },
        '&:hover': {
            backgroundColor: theme.sections.servicePresentation.item.backgroundLight,
            color: theme.sections.serviceUnpopular.foregroundDark,
        },
        '& span': {
            backgroundColor: theme.sections.serviceUnpopular.foregroundDark,
            color: theme.sections.serviceUnpopular.foregroundLight,
        },
    })

const lineStyle = (index: number) => (theme: Theme) => {
    const isOddLineOf4 = Math.ceil(index / 4) % 2 === 1
    const isOddLineOf2 = Math.ceil(index / 2) % 2 === 1

    return css({
        '&:nth-child(odd)': isOddLineOf4 ? greyStyle(theme) : greenStyle(theme),
        '&:nth-child(even)': isOddLineOf4 ? greenStyle(theme) : greyStyle(theme),
        [theme.breakpoints.down(theme.breakpoints.tablet)]: {
            '&:nth-child(odd)': greyStyle(theme),
            '&:nth-child(even)': greenStyle(theme),
        },
        [theme.breakpoints.down(theme.breakpoints.md)]: {
            '&:nth-child(odd)': isOddLineOf2 ? greyStyle(theme) : greenStyle(theme),
            '&:nth-child(even)': isOddLineOf2 ? greenStyle(theme) : greyStyle(theme),
        },
        [theme.breakpoints.down(theme.breakpoints.smartphone)]: {
            '&:nth-child(odd)': greyStyle(theme),
            '&:nth-child(even)': greenStyle(theme),
        },
    })
}

const badgeStyle = (theme: Theme) =>
    css({
        fontFamily: theme.fonts.default,
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '14px',
        lineHeight: '18px',
        borderRadius: '18px',
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingTop: '3px',
        paddingBottom: '3px',
    })

interface ItemServiceUnpopularProps {
    categoryDescription_mg: string | null
    categoryDescription: string | null
    categorySlug: string | null
    categoryName_mg: string | null
    categoryName: string | null
    serviceCount?: number | null
    index: number
    personaSlug?: string | null
}

const CategItem: FC<ItemServiceUnpopularProps> = (props) => {
    const getIntlLabel = useIntlLabel()

    return (
        <Link
            to={`/services/${props.categorySlug}${
                props.personaSlug ? '?persona=' + props.personaSlug : ''
            }`}
            css={[containerStyle, lineStyle(props.index + 1)]}
        >
            <h3>{getIntlLabel(props, 'categoryName')}</h3>
            <p>{getIntlLabel(props, 'categoryDescription')}</p>
            {props.serviceCount && props.serviceCount > 0 && (
                <span css={badgeStyle}>{props.serviceCount}</span>
            )}
        </Link>
    )
}

export default CategItem
